import { BasketListingTemplate as template } from './basket-listing.template';
import { BasketListingStyles as styles } from './basket-listing.styles';
import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { basketIcon } from '../../../../styles/icons';
import { IEnumService } from '../../../../services/enum.service';
import { IVppaService } from '../../../../services';
import { IAuthService } from '../../../../services/auth.service';
import { IRfq } from '../../../../types';

const name = 'basket-listing';

export interface IBasketGrid {
  PROJECT_NAME: string;
  TICKER: string;
  UNIQUE_ID: string;
  TECHNOLOGY: string;
  ISO: string;
  SETTLEMENT_POINT: string;
  VOLUME_YEAR: number;
  VOLUME: number;
  FIXED_PRICE_LOWER_BOUND: number;
  FIXED_PRICE_UPPER_BOUND: number;
  START_DATE: number | Date;
  TENOR_MONTHS: number;
  RFQ_CREDIT_PROFILE: string;
  COLLATERAL: number;
  IMPLIED_CARBON_PRICE_MIN: number;
  IMPLIED_CARBON_PRICE_MAX: number;
  STATUS: string;
  PROJECT_OPERATIONAL_STATUS: string;
  PROJECT_ID: string;
  RFQ_ID: string;
  IS_STATISTICS: boolean;
  PRODUCT_TYPE: string;
  CONTRACT_TYPE: string;
}

@customElement({
  name,
  template,
  styles,
})
export class BasketListing extends FASTElement {
  @observable basketListingGrid: zeroAgGrid;
  @IEnumService enumService: IEnumService;
  @IVppaService vppaService: IVppaService;
  @IAuthService authService: IAuthService;

  @observable selectedRfq: IRfq = null;
  @observable executedIds: Array<string> = [];

  connectedCallback(): void {
    super.connectedCallback();
    const gridOptions = {
      onRowDoubleClicked: (row) => this.vppaService.openVppaDetailModal(row.data?.VPPA_ID),
      animateRows: true,
      suppressRowClickSelection: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      onRowSelected: (params) => this.rowSelected(params),
      rowClassRules: {
        'row-dark': (params) => params.data?.RFQ_ID,
      },
      overlayNoRowsTemplate: `<span class="ag-overlay-no-rows-center">${basketIcon(
        'start',
      )}    Your basket is currently empty</span>`,
      tooltipShowDelay: 300,
    };

    this.basketListingGrid.gridOptions = gridOptions;
  }

  public rowSelected(event: any) {
    if (event && event.node && event.node.selected) {
      this.selectedRfq = event.data;
      this.$emit('proposeVppa', this.selectedRfq);
    }
  }

  public onGridReady() {
    this.basketListingGrid.gridApi.setRowData([]);
    this.basketListingGrid.gridApi.setPinnedTopRowData([
      {
        PROJECT_NAME: `No Project Selected`,
      },
    ]);
  }

  public clearBasketStatus() {
    this.selectedRfq = null;
    this.executedIds.length = 0;
  }

  public removeSelection() {
    this.selectedRfq = null;
    this.basketListingGrid.gridApi.deselectAll();
  }

  public setBasketData(
    basketList: Array<IBasketGrid>,
    project: IBasketGrid,
    basketStatistics: IBasketGrid,
  ) {
    if (project) {
      this.basketListingGrid.gridApi.setPinnedTopRowData([project]);
    } else {
      this.basketListingGrid.gridApi.setPinnedTopRowData([
        {
          PROJECT_NAME: 'No Project Selected',
        },
      ]);
    }
    if (basketList.length > 0) {
      this.basketListingGrid.gridApi.setPinnedBottomRowData([basketStatistics]);
    } else {
      this.basketListingGrid.gridApi.setPinnedBottomRowData([]);
    }
    this.basketListingGrid.gridApi.setRowData(basketList);
  }

  public downloadBasketCsv() {
    this.basketListingGrid.gridApi.exportDataAsCsv();
  }

  public getNextElement(rdqId: string) {
    this.executedIds.push(rdqId);
    this.basketListingGrid.gridApi.redrawRows();
    this.selectedRfq = null;
    const foundRfq = false;
    let getNextRfq = false;
    let nodeToSelect = null;
    this.basketListingGrid.gridApi.forEachNode((node) => {
      if (node.data.RFQ_ID === rdqId) {
        getNextRfq = true;
        return;
      }
      if (!this.executedIds.includes(node.data.RFQ_ID) && getNextRfq) {
        nodeToSelect = node;
        node.setSelected(true);
        getNextRfq = false;
        return;
      }
      if (!this.executedIds.includes(node.data.RFQ_ID) && !foundRfq && !nodeToSelect) {
        nodeToSelect = node;
        node.setSelected(true);
      }
    });
    if (!nodeToSelect) {
      this.basketListingGrid.gridApi.deselectAll();
      this.$emit('proposeVppa', this.selectedRfq);
      return;
    }
  }
}
